/* MinibarProducts.jsx — Minibar 9 ürünün TL + EUR fiyatlarını düzenleme sayfası.
 * Backend: GET /api/minibar/products (id + price_tl + price_eur dahil)
 *          PUT /api/products/{id} (mevcut Products PUT endpoint'ini kullanır)
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Loader2, Save, Coffee, ArrowLeft, CheckCircle2, AlertCircle } from 'lucide-react';
import { formatTRNumber } from '../lib/utils';

export default function MinibarProducts() {
    const navigate = useNavigate();
    const [rows, setRows] = useState([]); // [{id, code, name, unit, price_tl, price_eur}]
    const [loading, setLoading] = useState(true);
    const [savingId, setSavingId] = useState(null);
    const [savingAll, setSavingAll] = useState(false);
    const [dirty, setDirty] = useState({}); // {id: true}

    useEffect(() => {
        load();
    }, []);

    const load = async () => {
        setLoading(true);
        try {
            const res = await api.get('/minibar/products');
            setRows((res.data || []).map(r => ({
                id: r.id,
                code: r.code,
                name: r.name,
                unit: r.unit || 'Adet',
                price_tl: r.price_tl || 0,
                price_eur: r.price_eur || 0,
            })));
            setDirty({});
        } catch {
            toast.error('Minibar ürünleri yüklenemedi');
        } finally {
            setLoading(false);
        }
    };

    const setField = (id, field, value) => {
        const v = value === '' ? '' : Math.max(0, Number(value) || 0);
        setRows(prev => prev.map(r => r.id === id ? { ...r, [field]: v } : r));
        setDirty(prev => ({ ...prev, [id]: true }));
    };

    const saveRow = async (row) => {
        setSavingId(row.id);
        try {
            await api.put(`/products/${row.id}`, {
                name: row.name,
                code: row.code,
                sedna_code: row.code,
                unit: row.unit,
                price_tl: Number(row.price_tl) || 0,
                price_eur: Number(row.price_eur) || 0,
                category: 'minibar',
            });
            toast.success(`${row.name} fiyatları güncellendi`);
            setDirty(prev => {
                const n = { ...prev };
                delete n[row.id];
                return n;
            });
        } catch {
            toast.error('Kaydetme başarısız');
        } finally {
            setSavingId(null);
        }
    };

    const saveAll = async () => {
        setSavingAll(true);
        try {
            const dirtyIds = Object.keys(dirty);
            if (dirtyIds.length === 0) {
                toast.info('Değiştirilen ürün yok');
                return;
            }
            const targets = rows.filter(r => dirty[r.id]);
            await Promise.all(targets.map(r => api.put(`/products/${r.id}`, {
                name: r.name,
                code: r.code,
                sedna_code: r.code,
                unit: r.unit,
                price_tl: Number(r.price_tl) || 0,
                price_eur: Number(r.price_eur) || 0,
                category: 'minibar',
            })));
            toast.success(`${targets.length} ürün güncellendi`);
            setDirty({});
        } catch {
            toast.error('Toplu kaydetme başarısız');
        } finally {
            setSavingAll(false);
        }
    };

    const dirtyCount = Object.keys(dirty).length;
    const missingPriceCount = rows.filter(r => !(Number(r.price_tl) > 0)).length;

    return (
        <div className="space-y-4" data-testid="minibar-products-page">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                    <Button variant="ghost" size="icon" onClick={() => navigate('/minibar')} data-testid="back-btn">
                        <ArrowLeft className="w-5 h-5" />
                    </Button>
                    <div>
                        <h1 className="font-heading text-2xl font-bold text-slate-800 flex items-center gap-2">
                            <Coffee className="w-7 h-7 text-emerald-600" /> Minibar Ürün Fiyatları
                        </h1>
                        <p className="text-slate-500 text-sm mt-1">9 ürün — TL ve EUR birim fiyatı</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {dirtyCount > 0 && (
                        <span className="text-xs text-amber-700 bg-amber-50 border border-amber-300 rounded-md px-2.5 py-1">
                            {dirtyCount} değişiklik bekliyor
                        </span>
                    )}
                    <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700"
                        onClick={saveAll} disabled={savingAll || dirtyCount === 0}
                        data-testid="save-all-btn">
                        {savingAll ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                        Tümünü Kaydet
                    </Button>
                </div>
            </div>

            {missingPriceCount > 0 && (
                <div className="flex items-start gap-2 p-3 rounded-md bg-amber-50 border border-amber-300 text-amber-800 text-sm" data-testid="missing-price-warning">
                    <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                    <div><b>{missingPriceCount}</b> ürünün TL fiyatı henüz tanımlanmadı. Raporlarda 0₺ olarak görünür.</div>
                </div>
            )}

            <Card>
                <CardHeader className="pb-3">
                    <CardTitle className="text-base">Ürün Listesi</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                    {loading ? (
                        <div className="text-center py-12 text-slate-400">
                            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
                            Yükleniyor...
                        </div>
                    ) : (
                        <div className="overflow-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-slate-100">
                                    <tr>
                                        <th className="text-left p-3 font-medium text-slate-600 w-20">Kod</th>
                                        <th className="text-left p-3 font-medium text-slate-600">Ürün Adı</th>
                                        <th className="text-left p-3 font-medium text-slate-600 w-24">Birim</th>
                                        <th className="text-right p-3 font-medium text-slate-600 w-40">Birim Fiyat (₺)</th>
                                        <th className="text-right p-3 font-medium text-slate-600 w-40">Birim Fiyat (€)</th>
                                        <th className="text-center p-3 font-medium text-slate-600 w-32">İşlem</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map(r => {
                                        const isDirty = !!dirty[r.id];
                                        const noPrice = !(Number(r.price_tl) > 0);
                                        return (
                                            <tr key={r.id} className={`border-b hover:bg-slate-50 ${isDirty ? 'bg-amber-50/40' : ''}`} data-testid={`row-${r.code}`}>
                                                <td className="p-3 font-mono text-slate-700">{r.code}</td>
                                                <td className="p-3 text-slate-800 font-medium">
                                                    {r.name}
                                                    {noPrice && <span className="ml-2 inline-flex items-center text-xs text-amber-700">⚠️ fiyatsız</span>}
                                                </td>
                                                <td className="p-3 text-slate-600">{r.unit}</td>
                                                <td className="p-2">
                                                    <Input
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        value={r.price_tl}
                                                        onChange={(e) => setField(r.id, 'price_tl', e.target.value)}
                                                        className="h-9 text-right"
                                                        data-testid={`price-tl-${r.code}`}
                                                    />
                                                </td>
                                                <td className="p-2">
                                                    <Input
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        value={r.price_eur}
                                                        onChange={(e) => setField(r.id, 'price_eur', e.target.value)}
                                                        className="h-9 text-right"
                                                        data-testid={`price-eur-${r.code}`}
                                                    />
                                                </td>
                                                <td className="p-2 text-center">
                                                    <Button
                                                        size="sm"
                                                        variant={isDirty ? 'default' : 'outline'}
                                                        className={isDirty ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                                                        onClick={() => saveRow(r)}
                                                        disabled={savingId === r.id || !isDirty}
                                                        data-testid={`save-${r.code}`}
                                                    >
                                                        {savingId === r.id ? (
                                                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                        ) : isDirty ? (
                                                            <><Save className="w-3.5 h-3.5 mr-1" /> Kaydet</>
                                                        ) : (
                                                            <><CheckCircle2 className="w-3.5 h-3.5 mr-1 text-emerald-600" /> Kayıtlı</>
                                                        )}
                                                    </Button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                                <tfoot className="bg-slate-50 border-t">
                                    <tr>
                                        <td colSpan={3} className="p-3 text-right text-slate-600 font-medium">Toplam (örnek birim × 1):</td>
                                        <td className="p-3 text-right font-bold text-slate-800">
                                            {formatTRNumber(rows.reduce((s, r) => s + (Number(r.price_tl) || 0), 0), 2)} ₺
                                        </td>
                                        <td className="p-3 text-right font-bold text-slate-800">
                                            {formatTRNumber(rows.reduce((s, r) => s + (Number(r.price_eur) || 0), 0), 2)} €
                                        </td>
                                        <td></td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
